<template>
  <div>
    <ReportTable
      v-loading="tableLoading"
      :type="type"
      :title="title"
      :fields="fields"
      :items="items"
      :pageTotal="pageTotal"
      :currentPage="currentPage"
      :rows="rows"
      :path="path"
      :showHKSelect="false"
      :topExcel="true"
      :clientPagination="true"
      :stickyHeader="true"
      @filterPayloadChange="handleFilterPayloadChange"
      @handleSearch="handleSearch"
      @exportToExcel="exportToExcelFile()"
    />
  </div>
</template>
<script>
import ReportTable from './components/reportTable.vue'
import { getDayBoxOffice } from '@/api/specialRecord'

const WEEKDAY_TEXT = {
  1: '星期一',
  2: '星期二',
  3: '星期三',
  4: '星期四',
  5: '星期五',
  6: '星期六',
  7: '星期日',
}

export default {
  name: 'DayBoxOffice',
  components: {
    ReportTable,
  },
  data() {
    return {
      tableLoading: false,
      type: 'dayboxoffice',
      title: '22.6.2 最高票房日期（最好生意）',
      currentPage: 1,
      path: 'special-film-record',
      fields: [
        {
          key: 'rank',
          label: '排名',
          sortable: true,
        },
        {
          key: 'date',
          label: '日期',
          sortable: true,
        },
        {
          key: 'weekday_text',
          label: '星期',
          sortable: true,
        },
        {
          key: 'num_show',
          label: '場次',
          sortable: true,
        },
        {
          key: 'num_film',
          label: '放映影片數',
          sortable: true,
        },
        {
          key: 'bo',
          label: '票房',
          sortable: true,
        },
        {
          key: 'ad',
          label: '人次',
          sortable: true,
        },
      ],
      items: [],
      pageTotal: 0,
      rows: 1000,
      filters: {
        dateStart: this.$moment().subtract(1, 'months').format('YYYY-MM-DD'),
        dateEnd: this.$moment().subtract(1, 'days').format('YYYY-MM-DD'),
        weekday: [],
        month: [],
        specialDays: [],
        publicHoliday: '',
        weekdayHolidayLogic: 'and',
        theatreDays: [],
      },
    }
  },
  methods: {
    handleFilterPayloadChange(payload) {
      this.filters = payload
    },
    handleSearch() {
      this.currentPage = 1
      this.getReport()
    },
    // 後端只接受 Hong Kong / Macau / HongKong，而 sessionStorage 存的是小寫 'macau'
    getAreaParam() {
      const area = window.sessionStorage.getItem('area') || ''
      if (area === 'macau') {
        return 'Macau'
      }
      return area
    },
    getReport() {
      const { dateStart, dateEnd } = this.filters
      if (!dateStart || !dateEnd) {
        this.$message.warning('請先選擇日期範圍')
        return
      }
      if (this.$moment(dateEnd).isBefore(this.$moment(dateStart))) {
        this.$message.warning('結束日期不得早於開始日期')
        return
      }
      this.tableLoading = true
      const payload = {
        date_start: dateStart,
        date_end: dateEnd,
        weekday: this.filters.weekday || [],
        month: this.filters.month || [],
        special_days: this.filters.specialDays || [],
        // 戲院日：只保留日期命中所選日期的資料；空陣列 = 不篩選
        theatre_days: this.filters.theatreDays || [],
      }
      // public_holiday 三態：不傳 = 不篩選，故只在有選擇時才帶上（0 是有意義的取值）
      const holidaySet =
        this.filters.publicHoliday === '1' ||
        this.filters.publicHoliday === '0'
      if (holidaySet) {
        payload.public_holiday = Number(this.filters.publicHoliday)
      }
      // weekday_holiday_logic 只作用於 weekday 與 public_holiday 之間；不傳 = and
      const hasWeekday = (this.filters.weekday || []).length > 0
      if (
        hasWeekday &&
        holidaySet &&
        this.filters.weekdayHolidayLogic === 'or'
      ) {
        payload.weekday_holiday_logic = 'or'
      }
      const area = this.getAreaParam()
      if (area) {
        payload.area = area
      }
      getDayBoxOffice(payload)
        .then((res) => {
          // 文檔格式為 { result, data: [...] }；兼容直接返回數組的情況
          let list = []
          if (Array.isArray(res)) {
            list = res
          } else if (res && Array.isArray(res.data)) {
            list = res.data
          }
          // 後端已按票房降序返回，這裡不再重排；排名按返回順序
          this.items = list.map((el, index) => ({
            rank: index + 1,
            date: el.date,
            weekday_text: WEEKDAY_TEXT[el.weekday] || el.weekday,
            num_show: el.num_show,
            num_film: el.num_film,
            bo: el.bo,
            ad: el.ad,
          }))
          this.pageTotal = this.items.length
          this.tableLoading = false
        })
        .catch(() => {
          this.items = []
          this.pageTotal = 0
          this.tableLoading = false
        })
    },
    exportToExcelFile() {
      if (!this.items.length) {
        this.$message.warning('沒有可導出的數據')
        return
      }
      const header = this.fields.map((el) => el.label)
      const dataList = this.items.map((item) => [
        item.rank,
        item.date,
        item.weekday_text,
        Number(item.num_show),
        Number(item.num_film),
        Number(item.bo),
        Number(item.ad),
      ])
      dataList.unshift(header)
      const numFmtKeys = ['場次', '放映影片數', '票房', '人次']
      const numList = numFmtKeys.map((key) => ({
        title: this.getAlphabetLetter(header.indexOf(key)),
        index: 2,
      }))
      const excelInfo = {
        type: '22.6.2',
        merge: false,
        setDateValue: false,
        data: this.items,
        dateCol: [],
        fileName: `Highest B.O. Day Report (${this.filters.dateStart} to ${this.filters.dateEnd}).xlsx`,
        header,
        excelData: dataList,
        numFmt: true,
        numFmtList: numList,
        hasTotal: false,
      }
      this.excelFun(excelInfo, 'noapi')
    },
  },
}
</script>
